
import { Vue, Component, Watch } from 'vue-property-decorator';
import helperBox from '@/components/helper-box.vue';
import bindPhone from '@/components/bind-phone.vue';
import dialog from '@/components/dialog.vue';
import couponPicker from '@/components/coupon-picker.vue';
import loading from '@/components/loading.vue';
import rrybAdvert from '@/components/rryb-advert.vue';
import {
    Authorize,
    Plms,
    Coupon,
    Pay as PayService,
    PlateQuery,
    UserCenter,
    WechatNoneSence,
    CanIUseAlipay,
    CanIUseCMB,
} from '@/service';
import {
    ArrayExpand,
    token,
    tokenParse,
    userAgent,
    handleCoupon,
    encodeBase64,
} from '@/assets/ts/util';

// 支付倒计时默认值
const DEFAULT_COUNTDOWN = 60;

@Component({
    name: 'Pay',
    components: {
        rrybAdvert,
        helperBox,
        bindPhone,
        couponPicker,
        exceptionDialog: dialog,
        loading,
    },
    filters: {
        phone(val: string): string {
            if (!val) {
                return '';
            }
            return val.slice(0, 3) + '****' + val.slice(-4);
        },
    },
})
export default class Pay extends Vue {
    public moment!: any;
    public $message!: any;
    public $confirm!: any;
    public userAgent: any = userAgent();

    private readonly timeFormat: string = 'YYYY-MM-DD HH:mm:ss';
    private countdownNumber: number = 60;
    private timer: any = 0;
    private showTips: boolean = false;
    private rebate: any[] = [];
    private currentRebate: any = null;
    private coupon: any[] = [];
    private currentCoupon: any = null;
    // 支付方式
    private payment: string = '';
    // 免密支付 默认开启
    private openPayNoPassword: boolean = false;
    // 免密支付是否可用
    private canIUsePayNoPassword: boolean = false;
    // 绑定手机号弹窗显示
    private showBindPhone: boolean = false;
    // 请求状态
    private loading: boolean = false;
    // token 荷载信息
    private userInfo: any = tokenParse();
    // 异常信息弹窗
    private exceptionDialogShow: boolean = false;
    // 重新查询车费异常
    private requeryPlateError: boolean = false;
    // 优惠券选择窗
    private showCouponPicker: boolean = false;
    // 优惠券展示标签页
    private couponPickerTab: 'coupon' | 'rebate' = 'coupon';
    // 禁止使用支付宝
    private alipayDisabledParks: number[] = [];
    // 禁止使用一网通
    private cmbDisabledParks: number[] = [];

    get detail(): any {
        return this.$store.state.queryResult;
    }

    // 广告是否隐藏（禁投名单命中当前车场 detail.park_id 或 -1 全部，见 store getter）
    // @author logic
    // @date 2026-10-09
    get advertHidden(): boolean {
        return this.$store.getters.advertHidden;
    }

    get park(): any {
        return this.$store.state.park;
    }

    get lastMoney(): number {
        return Math.round((this.detail.pay_money - this.discount) * 100) / 100
            || 0;
    }

    get discount(): number {
        const coupon = this.currentCoupon;
        const rebate = this.currentRebate;
        const self = this;
        let discount = 0;

        function computedDiscount(data: any): number {
            let dis = 0;

            // 金额券
            if ([0, 3].includes(data.c_type)) {
                dis = Number(data.amount);
                if (dis > self.detail.pay_money) {
                    dis = self.detail.pay_money;
                }
            } else {
                const off = data.c_type === 4
                    ? (1 - Number(data.amount) / 100)  // 折扣券
                    : 1; // 全免券
                dis = self.detail.pay_money * off; // 全免券
            }
            return Math.round(dis * 100) / 100;
        }

        if (coupon?.magic_id) {
            discount = computedDiscount(coupon);
        } else if (rebate?.magic_id) {
            discount = computedDiscount(rebate);
        }

        return discount;
    }

    get supportRebate(): boolean {
        return !!this.park.is_platform_coupon;
    }

    get supportCoupon(): boolean {
        return !!this.park.is_merchant_coupon;
    }

    get canAlipay(): boolean {
        if (this.alipayDisabledParks?.[0] === -1) {
            return false;
        }
        return ['Alipay', 'H5', 'AlipayMiniProgram'].includes(userAgent())
            && !this.alipayDisabledParks.includes(this.detail.park_id);
    }

    get canWepay(): boolean {
        return true;
    }

    // 招行一网通
    get canCMB(): boolean {
        if (this.cmbDisabledParks?.[0] === -1) {
            return false;
        }

        if (userAgent() === 'AlipayMiniProgram') {
            return false;
        }

        if (['test', 'prod'].includes(window.SERVER_CONFIG.APP_ENV)) {
            return !this.cmbDisabledParks.includes(this.detail.park_id);
        }
        return false;
    }

    get exceptionMessage(): string | true {
        const { pay_money, keep_time } = this.detail;
        if (keep_time >= 10 * 24 * 60) {
            return `停车时长${keep_time}分钟`;
        } else if (pay_money >= 100) {
            return `停车费用${pay_money}元`;
        } else {
            return true;
        }
    }

    // 用户是否已开通免密支付
    get isOpenPayNoPassword() {
        return this.$store.state.isOpenPayNoPassword;
    }

    @Watch('currentRebate')
    public currentRebateChange(n: any, o: any): void {
        // pay_platform  '支付第三方平台,
        // 1-alipay, 2-wxpay, 3-balance(余额支付), 4-招行(一网通), 5-招行云停车'
        if (n?.pay_platform === 4) {
            this.payment = 'CMB';
        }
    }

    /**
     * 关闭优惠券使用提示
     */
    public closeTips(): void {
        this.showTips = false;
    }

    /**
     * 优惠券入口信息展示
     */
    public couponText(current: any, len: number): string {
        if (current?.magic_id) {
            return `${this.discount}元`;
        } else {
            return len ? `${len}张可用` : '暂无可用';
        }
    }

    /**
     * 设置默认支付方式
     */
    public setDefaultPayment(): string {
        let payment = localStorage.getItem('_rryb_webpay_payment_') || '';
        if (!payment) {
            switch (userAgent()) {
                case 'Wechat':
                    payment = 'Wepay';
                    break;
                case 'Alipay':
                    payment = 'Alipay';
                case 'H5':
                    // no break;
                default:
                    payment = 'Wepay';
                    break;
            }
        }
        return payment;
    }

    /**
     * 查询车费
     * @param {boolean} onlyQuery
     */
    public async query(onlyQuery: boolean = false) {
        this.loading = true;
        try {
            const { data } = await PlateQuery({
                plate_number: encodeURIComponent(this.detail.plate),
            });

            this.requeryPlateError = false;
            this.$store.commit('QUERY_RESULT', data);

            if (onlyQuery) {
                this.countdown();
            } else {
                this.init();
            }
        } catch (err) {
            console.warn(err);
            this.requeryPlateError = true;
        } finally {
            this.loading = false;
        }
    }

    /**
     * 缓存当前支付方式
     */
    public storageDefaultPayment(payment: string): void {
        localStorage.setItem('_rryb_webpay_payment_', payment);
    }

    /**
     * 切换支付方式
     */
    public changePayment(payment: string): void {
        this.payment = payment;
    }

    /**
     * 绑定手机号
     */
    public async onBindPhone(param: { phone: string, code: string }) {
        let action!: any;

        switch (userAgent()) {
            case 'Alipay':
                // no break;
            case 'Wechat':
                action = UserCenter.bindPhone;
                break;
            case 'H5':
                // no break;
            default:
                action = UserCenter.loginWithPhone;
                break;
        }

        this.loading = true;

        try {
            const res = await action(param);
            token(res.data.token);
            this.userInfo = tokenParse();
            this.showBindPhone = false;
            this.query();
        } catch (err) {
            console.warn(err);
        } finally {
            this.loading = false;
        }
    }

    /**
     * 关闭异常提示窗
     */
    public exceptionDialogCancel(): void {
        this.exceptionDialogShow = false;
        this.$router.replace({ name: 'home' });
    }

    /**
     * 获取停车场信息、优惠券信息
     */
    public init(): void {
        this.countdownNumber = DEFAULT_COUNTDOWN;
        this.countdown();

        // 提示异常停车数据 10天或100元
        if (this.exceptionMessage !== true) {
            this.exceptionDialogShow = true;
        }

        // 已登录用户
        if (token()) {
            const all = [
                Plms.parkDetail({ park_id: this.detail.park_id }),
                Coupon.usableCoupon(),
            ];

            this.loading = true;
            Promise.all(all).then(([plms, coupon]: any[]) => {
                this.loading = false;
                if (plms.rt_code === 0 && coupon.rt_code === 0) {
                    let couponList = coupon.data || [];

                    if (couponList.length) {
                        couponList = couponList.filter((v: any) => v.coupon_type === 0);
                    }

                    this.$store.commit('PARK_DETAIL', plms.data);
                    this.$store.commit('COUPON_LIST', couponList);
                    this.couponFormatter(couponList);
                } else {
                    this.$message('车场数据与优惠信息读取失败');
                    console.warn('车场数据与优惠信息读取失败:', plms, coupon);
                }
            }).catch((err: any) => {
                this.loading = false;
                this.$message('车场数据与优惠信息读取失败');
                console.warn('车场数据与优惠信息读取失败:', err);
            });
            // if (userAgent() === 'Wechat') {
            //     this.getPayNoPasswordStatus();
            // }
        }
        Promise.all([
            CanIUseAlipay(),
            CanIUseCMB(),
        ]).then(([alipay, cmb]: any) => {
            this.alipayDisabledParks = JSON.parse(alipay.data?.option_value ?? '[]');
            this.cmbDisabledParks = JSON.parse(cmb.data?.option_value ?? '[]');
        }).catch((error: any) => {
            this.alipayDisabledParks = [-1];
            this.cmbDisabledParks = [-1];
        });
    }

    /**
     * 支付倒计时
     */
    public countdown(): void {
        if (this.timer) {
            clearTimeout(this.timer);
        }

        if (this.countdownNumber) {
            this.timer = setTimeout(() => {
                this.countdownNumber--;
                this.countdown();
            }, 1000);
        } else {
            if (this.exceptionDialogShow) {
                this.exceptionDialogShow = false;
            }
            this.$confirm({
                title: '提示',
                message: '查询超时,请重新查询',
                showCancelButton: false,
                layer: true,
            }).then(() => {
                this.countdownNumber = DEFAULT_COUNTDOWN;
                this.query(true);
            }).catch(() => {
                // do nothing
            });
        }
    }

    /**
     * 获取用户免密支付开通状态
     */
    public async getPayNoPasswordStatus() {
        const userInfo = tokenParse();

        if (!userInfo) {
            return;
        }
        try {
            const { rt_code, data, msg } = await WechatNoneSence.check({
                openid: userInfo.openid,
                plate: this.detail.plate,
            });
            const userState = data ? data.user_state : '';
            const packageData = data ? data.package : {};
            switch (Number(rt_code)) {
                case 0: // 已开通微信无感支付
                    switch (userState.toUpperCase()) {
                        // 免密支付
                        case 'PROACTIVE':
                            // no break;
                        // 无感支付
                        case 'AUTOPAY':
                            // this.isOpenPayNoPassword = true
                            this.canIUsePayNoPassword = packageData.extraData.openid === userInfo.openid;
                            this.$store.commit('USER_IS_OPEN_PAY_NO_PASSWORD', true);
                            break;
                        // 已暂停
                        case 'PAUSED':
                            // no break;
                        // 欠费
                        case 'OVERDUE':
                            // no break;
                        // 未授权
                        case 'UNAUTHORIZED':
                            // no break;
                        default:
                            this.canIUsePayNoPassword = false;
                            this.$store.commit('USER_IS_OPEN_PAY_NO_PASSWORD', false);
                            break;
                    }
                    break;
                case 1: // 系统异常
                    // no break;
                case 2: // 微信查询失败
                    // no break;
                default:
                    this.$message(msg);
                    this.$store.commit('USER_IS_OPEN_PAY_NO_PASSWORD', false);
                    break;
            }
        } catch (err) {
            console.warn(err);
        }
    }

    // 选择优惠券
    public openCouponPicker(tab: 'coupon' | 'rebate') {
        this.couponPickerTab = tab;
        this.showCouponPicker = true;
    }

    /**
     * 优惠券前置处理
     */
    public async couponFormatter(data: any[]) {
        if (this.supportRebate) {
            let rebate = data.filter((v: any) => v.park_id === 0 && this.canIUseCoupon(v));

            rebate = rebate.length ? this.couponSort(handleCoupon(rebate)) : [];
            this.rebate = rebate;
            this.$store.commit('USABLE_REBATE', rebate);
        } else {
            this.rebate = [];
            this.$store.commit('USABLE_REBATE', []);
        }

        if (this.supportCoupon) {
            let coupon = data.filter((v: any) =>
                v.park_id === this.detail.park_id && this.canIUseCoupon(v));

            coupon = coupon.length ? this.couponSort(handleCoupon(coupon)) : [];
            this.coupon = coupon;
            this.$store.commit('USABLE_COUPON', this.supportCoupon ? coupon : []);

            if (coupon.length) {
                this.autoUseCoupon(coupon);
            }
        } else {
            this.coupon = [];
            this.$store.commit('USABLE_COUPON', []);
            this.currentCoupon = {};
        }
    }

    /**
     * 过滤可用券
     */
    public canIUseCoupon(data: any): boolean {
        const { c_type, pattern, amount } = data;
        switch (c_type) {
            case 0: // 金额券
                return pattern === 1
                    ? +amount >= this.detail.pay_money ? true : false
                    : true;
            case 1: // 小时券
                return  pattern === 1
                    ? +amount >= this.detail.keep_time ? true : false
                    : true;
            case 2: // 全面券
                // no break
            case 3: // 随机金额券
                return true;
            case 4: // 折扣券
                return true;
            default:
                return false;
        }
    }

    /**
     * 券排序
     */
    public couponSort(data: any[]): any[] {
        // 对券类型排序，全免>时间>金额(随机金额与金额券并做金额券)
        data.forEach((item: any) => {
            item.cTypeCommit = [0, 3].includes(item.c_type) ? 0 : item.c_type;
        });
        const groupByCType = ArrayExpand.groupByAll(data, 'cTypeCommit', 'desc');

        let result: any[] = [];
        // 对券内面值排序 降序
        for (let source of groupByCType.groups) {
            const groupByAmount = ArrayExpand.groupByAll(source, 'amount', 'desc');
            source = groupByAmount.result.concat();
            for (const group of groupByAmount.groups) {
                group.sort((prev: any, next: any) => prev.deadTime - next.deadTime);
                result = result.concat(...group);
            }
        }

        return result;
    }

    /**
     * 自动选择最优商户券
     */
    public autoUseCoupon(data: any[]): void {
        if (!data.length || !data) {
            return;
        }

        switch (data[0].c_type) {
            case 0: // 金额券
            case 3: // 随机金额券
                const money = this.detail.pay_money;
                let moneyCoupon = data.filter((v: any) =>
                    (+v.c_type === 0 || +v.c_type === 3) && Number(v.amount) >= money);

                // 与停车费最相近的面值
                if (!moneyCoupon.length) {
                    moneyCoupon = data
                        .filter((v: any) => +v.c_type === 0 || +v.c_type === 3);
                    moneyCoupon
                        .sort((prev, next) => Number(next.amount) - prev.amount);
                } else {
                    moneyCoupon.sort((prev, next) => Number(prev.amount) - next.amount);
                }
                this.currentCoupon = moneyCoupon[0];
                break;
            case 1: // 时间券
                const timeCoupon = data.filter((v: any) => v.c_type === 1); // 时间券一定不小于停车时长
                timeCoupon.sort((prev, next) => Number(prev.amount) - next.amount);
                this.currentCoupon = timeCoupon[0];
                break;
            case 2: // 全免券
                this.currentCoupon = data[0];
                break;
            case 4: // 折扣券
                this.currentCoupon = data[0];
                break;
        }
    }

    /**
     * 支付
     */
    public async pay() {
        if (this.loading) {
            return;
        }

        this.storageDefaultPayment(this.payment);
        const couponIds = [];
        const coupon = this.currentCoupon;
        const rebate = this.currentRebate;

        if (coupon?.magic_id) {
            couponIds.push(coupon.magic_id);
        }
        if (rebate?.magic_id) {
            couponIds.push(rebate.magic_id);
        }

        if (this.payment === 'Alipay') {
            PayService.alipay({
                pay_id: this.detail.pay_id,
                coupon_ids: couponIds.length ? couponIds.join('-') : '',
            });
        } else if (this.payment === 'CMB') {
            PayService.CMBpay({
                'pay_id': this.detail.pay_id,
                'coupon_ids': couponIds.length ? couponIds.join('-') : '',
                'RRYB-Auth': token(),
            });

        } else if (this.payment === 'Wepay') {
            if (userAgent() === 'Wechat') {
                this.loading = true;
                try {
                    const param: any = {
                        pay_id: this.detail.pay_id,
                        coupon_ids: couponIds.join('-'),
                    };
                    /*
                        currentPayNoPassword: 是否使用免密支付
                        已开通免密支付则往后都只必须使用免密支付
                        所以最终判定是否使用免密支付的条件是同意开通免密支付或者已经开通免密支付
                        一经开通无论此次支付是否使用免密支付都会调起免密支付
                    */
                    const currentPayNoPassword = this.park.is_wechat_auto_pay
                        && this.isOpenPayNoPassword
                        && this.canIUsePayNoPassword;
                    let wepayRes!: any;
                    if (currentPayNoPassword) {
                        wepayRes = await PayService.wechatNoPassword(param);
                    } else {
                        param.open_auto_pay = this.openPayNoPassword ? 1 : 0;
                        wepayRes = await PayService.wepay(param);
                    }
                    const { data, rt_code, msg } = wepayRes;

                    switch (rt_code) {
                        case 0:
                            WeixinJSBridge.invoke(
                                'getBrandWCPayRequest',
                                {
                                    appId: data.appId,
                                    timeStamp: data.timeStamp,
                                    nonceStr: data.nonceStr,
                                    package: data.package,
                                    signType: data.signType,
                                    paySign: data.paySign,
                                },
                                (res: any) => {
                                    this.loading = false;
                                    if (res.err_msg === 'get_brand_wcpay_request:ok') {
                                        this.$message('支付成功');
                                        this.$router.replace({ name: 'success'});
                                    }  else {
                                        this.$message('支付失败');
                                    }
                                },
                            );
                            break;
                        case 1: // 直接成功
                            this.loading = false;
                            this.$router.replace({ name: 'success' });
                            break;
                        default:
                            this.loading = false;
                            this.$message(msg);
                            this.$router.replace({ name: 'home' });
                            break;
                    }
                } catch (err) {
                    this.loading = false;
                    console.warn(err);
                }
            } else {
                this.$message.warning('如需微信支付请使用微信打开');
            }
        }
    }

    /**
     * 跳转授权并携带车牌参数自动查询
     */
    public authorizeBeforePay() {
        const callback = location.origin
            + (location.search ? '&' : '?')
            + `repeat=1&plate=${this.detail.plate}`;
        console.log('callback:' + callback);
        Authorize(encodeBase64(callback));
    }

    private created() {
        if (!this.detail.plate) {
            this.$router.replace({ name: 'home' });
            return;
        }
        this.payment = this.setDefaultPayment();
    }

    private beforeRouteLeave(to: any, from: any, next: any) {
        if (this.showCouponPicker) {
            this.showCouponPicker = false;
            next(false);
        } else {
            if (this.timer) {
                clearTimeout(this.timer);
            }
            if (this.exceptionDialogShow) {
                this.exceptionDialogShow = false;
            }
            next();
        }
    }

    private beforeRouteEnter(to: any, from: any, next: any) {
        next((vm: any) => {
            if (vm.detail.plate) {
                vm.init();
            } else {
                this.$router.replace({ name: 'home' });
            }
        });
    }
}
